import React from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SvgFragment from '../../../../fragments/SvgFragment';
import { styles } from '../../../../styles/themes/style';
import { CAMPAIGN_TEMPLATE_VARIABLES } from '../../../../utils/campaignTemplateVariables';

interface Props {
  show: boolean;
  tokens: string[];
  includeSignature?: boolean;
  onHide: () => void;
  onConfirm: () => void;
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function unsupportedFormatLead(count: number): string {
  return count === 1
    ? '1 variable uses an unsupported format. Update it before continuing.'
    : `${count} variables use an unsupported format. Update them before continuing.`;
}

const CampaignVariableWarnModal: React.FC<Props> = ({
  show, tokens, includeSignature, onHide, onConfirm,
}) => (
  <StyledModal show={show} onHide={onHide} width="583px" backdrop="static">
    <Body>
      <Header>
        <TitleRow>
          <ModalInfoIcon><SvgFragment type="campaignVariableInfo" /></ModalInfoIcon>
          <Title>Unsupported variables found</Title>
        </TitleRow>
        <CloseBtn type="button" onClick={onHide} aria-label="Close">
          <SvgFragment type="close" />
        </CloseBtn>
      </Header>
      <Lead>{unsupportedFormatLead(tokens.length)}</Lead>
      <Section>
        <Label>Unsupported formats</Label>
        <Chips>
          {tokens.map(token => (
            <BadToken key={token}>{token}</BadToken>
          ))}
        </Chips>
      </Section>
      <Section>
        <Label>Supported formats</Label>
        <Chips>
          {CAMPAIGN_TEMPLATE_VARIABLES.map(v => (
            <Chip key={v.key}>{v.token}</Chip>
          ))}
          {includeSignature && <Chip>{'{{signature}}'}</Chip>}
        </Chips>
      </Section>
      <Footer>
        <GhostBtn type="button" onClick={onHide}>Go back</GhostBtn>
        <PrimaryBtn type="button" onClick={onConfirm}>Continue anyway</PrimaryBtn>
      </Footer>
    </Body>
  </StyledModal>
);

export default CampaignVariableWarnModal;

/** The same warning, under the field, while the message is being written. */
export const CampaignVariableWarnBanner: React.FC<{ tokens: string[] }> = ({ tokens }) => {
  if (!tokens.length) return null;
  const count = tokens.length;
  return (
    <Banner>
      <BannerInfoIcon><SvgFragment type="campaignVariableInfo" /></BannerInfoIcon>
      <BannerBody>
        <BannerCopy>
          <BannerTitle>
            {countLabel(count, 'unsupported variable found', 'unsupported variables found')}
          </BannerTitle>
          <BannerText>
            {count === 1
              ? 'This variable uses an unsupported format and will be sent as written.'
              : 'These variables use an unsupported format and will be sent as written.'}
          </BannerText>
        </BannerCopy>
        <BannerChips>
          {tokens.map(token => (
            <BannerToken key={token}>{token}</BannerToken>
          ))}
        </BannerChips>
      </BannerBody>
    </Banner>
  );
};

const ModalInfoIcon = styled.span`
  flex: 0 0 auto;
  display: flex;
  width: 18px;
  height: 18px;
  .svg { width: 18px; height: 18px; display: block; }
`;

const BannerInfoIcon = styled.span`
  flex: 0 0 auto;
  display: flex;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  .svg { width: 14px; height: 14px; display: block; }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 26px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  align-self: stretch;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-family: NunitoSans;
  font-size: 18px;
  font-weight: 800;
  line-height: 23px;
  color: #333951;
`;

const CloseBtn = styled.button`
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 2px;
  line-height: 0;
  color: #8C96AD;
  .svg { width: 20px; height: 20px; }
  &:hover { color: #64748b; }
`;

const Lead = styled.p`
  margin: -8px 0 0;
  font-family: NunitoSans;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: #475569;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Label = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  color: #64748B;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BadToken = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  background: #F5D9D9;
  border-radius: 4px;
  font-family: NunitoSans;
  font-weight: 600;
  font-size: 12px;
  line-height: 19px;
  color: #CB5955;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  background: #EEF2FF;
  border-radius: 4px;
  font-family: NunitoSans;
  font-weight: 600;
  font-size: 12px;
  line-height: 19px;
  color: ${styles.colors.primary600};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: stretch;
  gap: 12px;
  padding-top: 4px;
`;

const GhostBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 9px 8px;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  color: #64748B;
  &:hover { color: #334155; }
`;

const PrimaryBtn = styled.button`
  border: none;
  border-radius: 8px;
  padding: 11px 20px;
  background: ${styles.colors.primary600};
  color: #fff;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  &:hover { background: ${styles.colors.primary400}; }
`;

const Banner = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  background: #FBF3F4;
  border: 1px solid #CB5955;
  border-radius: 10px;
`;

const BannerBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const BannerCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const BannerTitle = styled.div`
  font-family: NunitoSans;
  font-weight: 700;
  font-size: 14px;
  line-height: 19px;
  color: #333951;
`;

const BannerText = styled.p`
  margin: 0;
  font-family: NunitoSans;
  font-weight: 400;
  font-size: 12px;
  line-height: 19px;
  color: #333951;
`;

const BannerChips = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
`;

const BannerToken = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  background: #F5D9D9;
  border-radius: 4px;
  font-family: NunitoSans;
  font-weight: 600;
  font-size: 12px;
  line-height: 19px;
  color: #CB5955;
`;
